import React, { useState } from 'react';
import CombinedEditableSection from '../../../../../../../components/CombinedEditableSection.comp';
import { parseInlineMarkdown } from '../../../../../../utils/LearnTab/slideTextRenderer';
import { tUi } from '../../../../../../../../../utils/dq_lc';
import {
  HtmlContentLayout,
  HtmlImageContainer,
  HtmlContentColumn,
  HtmlContentWrapper,
  ImagePlaceholder,
  SlideTitle,
} from '../../../../../../styles/LearnTab/slideBase.styles';
import { SlideTitleHeader, isSameAsTitle } from '../../common/SlideTitleHeader.comp';
import { renderTextWithId } from '../../common/EditableSlideField.comp';
import { SlideImageLightboxModal } from '../../common/SlideImageLightbox.comp';
import CaseStudyCard from './CaseStudyCard.comp';
import HandsOnLabCard from './HandsOnLabCard.comp';
import PracticeOverviewCard from './PracticeOverviewCard.comp';
import ThemeMapCard from './ThemeMapCard.comp';
import ExampleArtifactCard from './ExampleArtifactCard.comp';
import PrepGuideCard from './PrepGuideCard.comp';
import WhyOutcomeCards from './WhyOutcomeCards.comp';

export const HtmlContentSlide = ({
  slide,
  highlightWord,
  isEditMode = false,
  slideEdits = {},
  showEnglishContent = false,
  onSaveEdit,
  onResetEdit,
  onCancelEdit,
  onImageClick,
  pollFeedback = null,
  isPollCorrect = false,
  uiLocale = 'en-US',
}) => {
  const [internalLightbox, setInternalLightbox] = useState(null);
  const origin = slide?._pbdq_origin || slide || {};
  const slideType = origin.slide_type || slide?._pbdq_kind;

  const isCaseStudy =
    slideType === 'case_study' ||
    slide?._pbdq_kind === 'case_study' ||
    origin.slide_type === 'case_study' ||
    slide?.slide_type === 'case_study' ||
    Boolean(origin.case_snippet || origin.case_title || slide?.case_snippet || slide?.case_title);

  const handleImgClick = (img) => {
    if (onImageClick) {
      onImageClick(img);
    } else {
      setInternalLightbox(img);
    }
  };

  const textRendererOptions = { slideEdits, showEnglishContent, highlightWord };

  // 1. Case Study
  if (!isEditMode && (isCaseStudy || slideType === 'case_study' || origin.case_snippet || origin.case_title)) {
    return (
      <CaseStudyCard
        slide={slide}
        slideEdits={slideEdits}
        showEnglishContent={showEnglishContent}
        highlightWord={highlightWord}
        onImageClick={handleImgClick}
        uiLocale={uiLocale}
      />
    );
  }

  // 1b. Hands-on Lab (full width, no image)
  if (!isEditMode && slideType === 'hands_on_lab') {
    return (
      <HandsOnLabCard
        slide={slide}
        slideEdits={slideEdits}
        showEnglishContent={showEnglishContent}
        highlightWord={highlightWord}
        uiLocale={uiLocale}
      />
    );
  }

  // 1c. Practice overview (first slide of a practice session, full width, no image)
  if (!isEditMode && slideType === 'practice_overview') {
    return (
      <PracticeOverviewCard
        slide={slide}
        slideEdits={slideEdits}
        showEnglishContent={showEnglishContent}
        highlightWord={highlightWord}
        uiLocale={uiLocale}
      />
    );
  }

  // 1d. Skill map (ThemeFirst: the whole skill with today's parts marked)
  if (!isEditMode && slideType === 'theme_map') {
    return <ThemeMapCard slide={slide} uiLocale={uiLocale} />;
  }

  if (!isEditMode && slideType === 'example_artifact') {
    return <ExampleArtifactCard slide={slide} uiLocale={uiLocale} />;
  }

  // 1e. Before you start (self-study notes, last slide of the intro)
  if (!isEditMode && slideType === 'prep_guide') {
    return <PrepGuideCard slide={slide} uiLocale={uiLocale} />;
  }

  // 2. Why Outcomes
  if (!isEditMode && (slideType === 'why' || origin.positive_outcome || origin.if_you_do_this_well || origin.negative_outcome || origin.if_you_skip_this)) {
    const posImg = origin.positive_image_url || origin.positive_image || slide?.positive_image_url || null;
    const negImg = origin.negative_image_url || origin.negative_image || slide?.negative_image_url || null;
    const hasOutcomeImages = Boolean(posImg || negImg);
    const hasSideImage = !hasOutcomeImages && slide?.Images && slide.Images.length > 0;

    const rawTitle = slide?.SlideTitle || origin.headline || origin.title || '';
    const titleEdit = slideEdits?.['title'];
    const displayTitle = showEnglishContent ? (titleEdit?.original || rawTitle) : (titleEdit?.edited || rawTitle);

    const renderTitleElement = () => {
      if (!displayTitle || !displayTitle.trim()) return null;
      return (
        <SlideTitle>
          {highlightWord ? highlightWord('title', displayTitle) : displayTitle}
        </SlideTitle>
      );
    };

    if (hasSideImage) {
      return (
        <>
          <HtmlContentLayout>
            <HtmlImageContainer>
              <ImagePlaceholder>
                <img
                  src={slide.Images[0]}
                  alt="Slide image"
                  onClick={() => handleImgClick(slide.Images[0])}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    WebkitUserDrag: 'none',
                  }}
                />
              </ImagePlaceholder>
            </HtmlImageContainer>
            <HtmlContentColumn>
              {renderTitleElement()}
              <HtmlContentWrapper>
                <WhyOutcomeCards slide={slide} onImageClick={handleImgClick} uiLocale={uiLocale} />
              </HtmlContentWrapper>
            </HtmlContentColumn>
          </HtmlContentLayout>

          {!onImageClick && (
            <SlideImageLightboxModal
              image={internalLightbox}
              onClose={() => setInternalLightbox(null)}
            />
          )}
        </>
      );
    } else {
      return (
        <HtmlContentColumn style={{ width: '100%' }}>
          {renderTitleElement()}
          <HtmlContentWrapper style={{ width: '100%' }}>
            <WhyOutcomeCards slide={slide} onImageClick={handleImgClick} uiLocale={uiLocale} />
          </HtmlContentWrapper>
        </HtmlContentColumn>
      );
    }
  }

  // 3. Scenario / Bridge / Story
  if (!isEditMode && (slideType === 'scenario' || slideType === 'bridge' || slideType === 'story_scenario' || slideType === 'story_bridge' || origin.scenario_text || origin.story_text)) {
    const title = slide?.SlideTitle || origin.scene_title || origin.bridge_title || origin.title || '';
    const scenarioText = origin.scenario_text || origin.story_text || '';
    const dialogue = origin.character_dialogue || origin.dialogue || '';
    const charName = origin.character_name || origin.character || '';
    const hasImage = slide?.Images && slide.Images.length > 0;

    const renderScenarioContent = () => (
      <>
        {scenarioText && (
          <div className="scenario-text">
            {parseInlineMarkdown(scenarioText)}
          </div>
        )}
        {dialogue && (
          <div className="speech-bubble">
            {charName ? <strong>{charName}: </strong> : null}
            {parseInlineMarkdown(dialogue)}
          </div>
        )}
      </>
    );

    return (
      <>
        {hasImage ? (
          <HtmlContentLayout>
            <HtmlImageContainer>
              <ImagePlaceholder>
                <img
                  src={slide.Images[0]}
                  alt="Slide image"
                  onClick={() => handleImgClick(slide.Images[0])}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    WebkitUserDrag: 'none',
                  }}
                />
              </ImagePlaceholder>
            </HtmlImageContainer>
            <HtmlContentColumn>
              {title && title.trim() && (
                <SlideTitle>
                  {highlightWord ? highlightWord('title', title) : title}
                </SlideTitle>
              )}
              <HtmlContentWrapper>
                {renderScenarioContent()}
              </HtmlContentWrapper>
            </HtmlContentColumn>
          </HtmlContentLayout>
        ) : (
          <HtmlContentColumn>
            {title && title.trim() && (
              <SlideTitle>
                {highlightWord ? highlightWord('title', title) : title}
              </SlideTitle>
            )}
            <HtmlContentWrapper>
              {renderScenarioContent()}
            </HtmlContentWrapper>
          </HtmlContentColumn>
        )}

        {!onImageClick && (
          <SlideImageLightboxModal
            image={internalLightbox}
            onClose={() => setInternalLightbox(null)}
          />
        )}
      </>
    );
  }

  // 4. Learning Objective (slo_title)
  if (!isEditMode && slideType === 'slo_title') {
    const sloTitleText = origin.title || origin.slo_title || origin.description_for_learner || '';
    const sloIcon = origin.icon || '';
    const promise = origin.promise_text || origin.promise || '';

    return (
      <HtmlContentColumn>
        <HtmlContentWrapper>
          <div className="slo-title-card">
            <div className="slo-title-header">
              {sloIcon && <div className="slo-title-icon">{sloIcon}</div>}
              <div className="slo-title-kicker">{tUi('slide.content.learningObjective', uiLocale)}</div>
            </div>
            {sloTitleText && <h1 className="slo-title-main">{parseInlineMarkdown(sloTitleText)}</h1>}
            {promise && <p className="content-body" style={{ textAlign: 'left', margin: 0 }}>{parseInlineMarkdown(promise)}</p>}
          </div>
        </HtmlContentWrapper>
      </HtmlContentColumn>
    );
  }

  // 5. Big Idea
  if (!isEditMode && (slideType === 'big_idea' || origin.big_idea)) {
    const headline = slide?.SlideTitle || origin.headline || origin.title || "Today's Question";
    const bigIdeaText = origin.big_idea || slide?.SlideContent || '';
    const subtext = origin.subtext || '';
    const hasImage = slide?.Images && slide.Images.length > 0;

    const renderBigIdeaContent = () => (
      <div className="big-idea-container" style={{ textAlign: 'left', marginTop: 10 }}>
        {bigIdeaText && (
          <h1 className="big-idea-text" style={{ fontSize: '2rem', fontWeight: 800, color: '#111827', margin: '0 0 16px 0', lineHeight: 1.3 }}>
            {parseInlineMarkdown(bigIdeaText)}
          </h1>
        )}
        {subtext && (
          <p className="big-idea-subtext" style={{ fontSize: '1.2rem', marginTop: '10px', color: '#4B5563', lineHeight: 1.5 }}>
            {parseInlineMarkdown(subtext)}
          </p>
        )}
      </div>
    );

    return (
      <>
        {hasImage ? (
          <HtmlContentLayout>
            <HtmlImageContainer>
              <ImagePlaceholder>
                <img
                  src={slide.Images[0]}
                  alt="Slide image"
                  onClick={() => handleImgClick(slide.Images[0])}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    WebkitUserDrag: 'none',
                  }}
                />
              </ImagePlaceholder>
            </HtmlImageContainer>
            <HtmlContentColumn>
              {headline && headline.trim() && (
                <SlideTitle>
                  {highlightWord ? highlightWord('title', headline) : headline}
                </SlideTitle>
              )}
              <HtmlContentWrapper>
                {renderBigIdeaContent()}
              </HtmlContentWrapper>
            </HtmlContentColumn>
          </HtmlContentLayout>
        ) : (
          <HtmlContentColumn>
            {headline && headline.trim() && (
              <SlideTitle>
                {highlightWord ? highlightWord('title', headline) : headline}
              </SlideTitle>
            )}
            <HtmlContentWrapper>
              {renderBigIdeaContent()}
            </HtmlContentWrapper>
          </HtmlContentColumn>
        )}

        {!onImageClick && (
          <SlideImageLightboxModal
            image={internalLightbox}
            onClose={() => setInternalLightbox(null)}
          />
        )}
      </>
    );
  }

  // 6. Reveal
  if (!isEditMode && (slideType === 'reveal' || origin.reveal_title || origin.correct_answer_text)) {
    const title = slide?.SlideTitle || origin.reveal_title || origin.title || '';
    const correctAnswer = origin.correct_answer_text || '';
    const reason = origin.one_sentence_reason || '';
    const action = origin.follow_up_action || '';
    const hasImage = slide?.Images && slide.Images.length > 0;
    const feedbackToDisplay = isPollCorrect ? null : (pollFeedback || null);

    const renderRevealContent = () => (
      <>
        {feedbackToDisplay && (
          <div className="content-feedback-chosen">
            {parseInlineMarkdown(feedbackToDisplay)}
          </div>
        )}
        <div className="reveal-big">
          <div className="answer-label">
            {isPollCorrect ? tUi('slide.reveal.correct', uiLocale) : tUi('slide.reveal.incorrect', uiLocale)}
          </div>
          {correctAnswer && <div className="answer">{parseInlineMarkdown(correctAnswer)}</div>}
          {reason && <div className="reason">{parseInlineMarkdown(reason)}</div>}
        </div>
        {action && (
          <div className="content-so-what">
            {parseInlineMarkdown(action)}
          </div>
        )}
      </>
    );

    return (
      <>
        {hasImage ? (
          <HtmlContentLayout>
            <HtmlImageContainer>
              <ImagePlaceholder>
                <img
                  src={slide.Images[0]}
                  alt="Slide image"
                  onClick={() => handleImgClick(slide.Images[0])}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    WebkitUserDrag: 'none',
                  }}
                />
              </ImagePlaceholder>
            </HtmlImageContainer>
            <HtmlContentColumn>
              {/* {title && title.trim() && (
                <SlideTitle>
                  {highlightWord ? highlightWord('title', title) : title}
                </SlideTitle>
              )} */}
              <HtmlContentWrapper>
                {renderRevealContent()}
              </HtmlContentWrapper>
            </HtmlContentColumn>
          </HtmlContentLayout>
        ) : (
          <HtmlContentColumn>
            {/* {title && title.trim() && (
              <SlideTitle>
                {highlightWord ? highlightWord('title', title) : title}
              </SlideTitle>
            )} */}
            <HtmlContentWrapper>
              {renderRevealContent()}
            </HtmlContentWrapper>
          </HtmlContentColumn>
        )}

        {!onImageClick && (
          <SlideImageLightboxModal
            image={internalLightbox}
            onClose={() => setInternalLightbox(null)}
          />
        )}
      </>
    );
  }

  // 7. Celebration / Badge
  if (!isEditMode && (slideType === 'celebration' || slideType === 'badge' || origin.celebration_message || origin.badge_label)) {
    const badgeLabel = origin.badge_label || '';
    const celebrationMessage = origin.celebration_message || origin.achievement_line || origin.headline || '';
    const recaps = (origin.slo_recaps && origin.slo_recaps.length > 0)
      ? origin.slo_recaps
      : [origin.slo1_recap, origin.slo2_recap, origin.slo3_recap].filter(Boolean);
    const hasImage = slide?.Images && slide.Images.length > 0;

    const renderCelebrationContent = () => (
      <div className="celebration-container">
        {badgeLabel && (
          <div className="celebration-badge">
            {parseInlineMarkdown(badgeLabel)}
          </div>
        )}
        {celebrationMessage && (
          <h2 className="celebration-message">
            {parseInlineMarkdown(celebrationMessage)}
          </h2>
        )}
        {recaps && recaps.length > 0 && (
          <ul className="celebration-recaps">
            {recaps.map((recap, idx) => (
              <li key={idx}>
                <span>{parseInlineMarkdown(recap)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );

    return (
      <>
        {hasImage ? (
          <HtmlContentLayout>
            <HtmlImageContainer>
              <ImagePlaceholder>
                <img
                  src={slide.Images[0]}
                  alt="Slide image"
                  onClick={() => handleImgClick(slide.Images[0])}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    WebkitUserDrag: 'none',
                  }}
                />
              </ImagePlaceholder>
            </HtmlImageContainer>
            <HtmlContentColumn style={{ alignItems: 'center', textAlign: 'center' }}>
              <HtmlContentWrapper style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                {renderCelebrationContent()}
              </HtmlContentWrapper>
            </HtmlContentColumn>
          </HtmlContentLayout>
        ) : (
          <HtmlContentColumn style={{ alignItems: 'center', textAlign: 'center' }}>
            <HtmlContentWrapper style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              {renderCelebrationContent()}
            </HtmlContentWrapper>
          </HtmlContentColumn>
        )}

        {!onImageClick && (
          <SlideImageLightboxModal
            image={internalLightbox}
            onClose={() => setInternalLightbox(null)}
          />
        )}
      </>
    );
  }

  // 8. Summary
  if (!isEditMode && slideType === 'summary') {
    const headline = slide?.SlideTitle || origin.headline || origin.title || tUi('slide.content.summary', uiLocale);
    const recaps = origin.slo_recaps || [];
    return (
      <HtmlContentColumn>
        {headline && headline.trim() && (
          <SlideTitle>
            {highlightWord ? highlightWord('title', headline) : headline}
          </SlideTitle>
        )}
        <HtmlContentWrapper>
          {recaps && recaps.length > 0 && (
            <ul className="recap-list">
              {recaps.map((r, idx) => (
                <li key={idx}>{parseInlineMarkdown(r)}</li>
              ))}
            </ul>
          )}
        </HtmlContentWrapper>
      </HtmlContentColumn>
    );
  }

  // 9. Key Points
  if (!isEditMode && (slideType === 'key_points' || (Array.isArray(origin.points) && origin.points.length > 0 && origin.points[0]?.verb_phrase))) {
    const title = slide?.SlideTitle || origin.section_title || origin.title || '';
    const rawIntro = origin.intro_line || '';
    const intro = isSameAsTitle(rawIntro, slide) ? '' : rawIntro;
    const closing = origin.closing_line || '';
    const points = origin.points || [];

    return (
      <HtmlContentColumn>
        {title && title.trim() && (
          <SlideTitle>
            {highlightWord ? highlightWord('title', title) : title}
          </SlideTitle>
        )}
        <HtmlContentWrapper>
          {intro && (
            <div className="intro-line">
              {parseInlineMarkdown(intro)}
            </div>
          )}
          {points && points.length > 0 && (
            <ul className="key-points">
              {points.map((p, idx) => {
                const hasIcon = Boolean(typeof p.icon === 'string' ? p.icon.trim() : p.icon);
                return (
                  <li key={idx} className={hasIcon ? 'has-icon' : 'no-icon'}>
                    {hasIcon && <span className="icon">{p.icon}</span>}
                    <div className="key-point-copy">
                      <div className="key-point-phrase">
                        <strong>{parseInlineMarkdown(p.verb_phrase)}</strong>
                      </div>
                      {p.description && (
                        <div className="key-point-desc">
                          {parseInlineMarkdown(p.description)}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {closing && (
            <div className="key-points-closing">
              {parseInlineMarkdown(closing)}
            </div>
          )}
        </HtmlContentWrapper>
      </HtmlContentColumn>
    );
  }

  // 10. Content New / Content More / Content / Framework / Stakes / Guide
  const isSplitTail = slide?._split_tail || origin._split_tail;
  if (!isEditMode && (slideType === 'content_new' || slideType === 'content_more' || slideType === 'content' || slideType === 'framework' || slideType === 'stakes' || slideType === 'guide')) {
    const isContentMore = slideType === 'content_more' || slideType === 'stakes';
    const hook = isContentMore ? origin.high_stakes_moment : origin.hook;
    const soWhat = origin.so_what;

    const renderSlideMainContent = () => (
      <>
        {/* Hook / High Stakes Moment */}
        {hook && (
          <p className="content-hook">
            {parseInlineMarkdown(hook)}
          </p>
        )}

        {/* Content New & standard content layouts */}
        {!isContentMore && !isSplitTail && (
          <>
            {/* Taxonomy Framework */}
            {origin.framework_type === 'taxonomy' && origin.taxonomy && (
              <div className="taxonomy-wrapper">
                {origin.taxonomy.map((item, idx) => (
                  <div key={idx} className="taxonomy-item">
                    <span className="taxonomy-number">{idx + 1}. </span>
                    <span className="taxonomy-title">{parseInlineMarkdown(item.name)}</span>
                    <span className="taxonomy-sep"> — </span>
                    <span className="taxonomy-explanation">{parseInlineMarkdown(item.explanation)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Spectrum Framework */}
            {origin.framework_type === 'spectrum' && origin.spectrum && (
              <div className="spectrum-container">
                <div className="spectrum-grid">
                  {origin.spectrum.wrong_mindset && (
                    <div className="spectrum-card wrong">
                      <div className="spectrum-label wrong">{tUi('slide.spectrum.wrongMindset', uiLocale)}</div>
                      <div className="spectrum-value wrong">
                        {parseInlineMarkdown(origin.spectrum.wrong_mindset)}
                      </div>
                    </div>
                  )}
                  {origin.spectrum.right_mindset && (
                    <div className="spectrum-card right">
                      <div className="spectrum-label right">{tUi('slide.spectrum.rightMindset', uiLocale)}</div>
                      <div className="spectrum-value right">
                        {parseInlineMarkdown(origin.spectrum.right_mindset)}
                      </div>
                    </div>
                  )}
                </div>
                {origin.spectrum.comparison_details && (
                  <div className="spectrum-details">
                    {parseInlineMarkdown(origin.spectrum.comparison_details)}
                  </div>
                )}
              </div>
            )}

            {origin.framework_type === 'sequence' && origin.sequence && (() => {
              const isT7 = origin.tier === 'T7' || slide?.tier === 'T7';
              return (
                <div className="sequence-container">
                  {/* Before Steps */}
                  {origin.sequence.filter(step => step.timing === 'Before').length > 0 && (
                    <div className="sequence-card before">
                      <div className="sequence-title before">
                        {isT7 ? tUi('slide.sequence.beforeOperations', uiLocale) : tUi('slide.sequence.firstSteps', uiLocale)}
                      </div>
                      <div className="sequence-steps">
                        {origin.sequence.filter(step => step.timing === 'Before').map((step, idx) => (
                          <div key={idx} className="sequence-step">
                            <span className="sequence-arrow">➔ </span>
                            <span className="sequence-action">{parseInlineMarkdown(step.action)}</span>:{' '}
                            <span className="sequence-explanation">{parseInlineMarkdown(step.explanation)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* After Steps */}
                  {origin.sequence.filter(step => step.timing === 'After').length > 0 && (
                    <div className="sequence-card after">
                      <div className="sequence-title after">
                        {isT7 ? tUi('slide.sequence.afterOperations', uiLocale) : tUi('slide.sequence.nextActions', uiLocale)}
                      </div>
                      <div className="sequence-steps">
                        {origin.sequence.filter(step => step.timing === 'After').map((step, idx) => (
                          <div key={idx} className="sequence-step">
                            <span className="sequence-arrow">➔ </span>
                            <span className="sequence-action">{parseInlineMarkdown(step.action)}</span>:{' '}
                            <span className="sequence-explanation">{parseInlineMarkdown(step.explanation)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Watch-Outs Modifier */}
            {origin.modifier_type === 'watch_outs' && origin.watch_outs && (
              <div className="watch-outs-container">
                <div className="watch-outs-title">{tUi('slide.modifier.watchOuts', uiLocale)}</div>
                {origin.watch_outs.map((w, idx) => (
                  <div key={idx} className="watch-out-item">
                    • {parseInlineMarkdown(w)}
                  </div>
                ))}
              </div>
            )}

            {/* Negative Space Modifier */}
            {origin.modifier_type === 'negative_space' && origin.negative_space && (
              <div className="negative-space-container">
                <strong>{tUi('slide.modifier.whatThisIsNot', uiLocale)}</strong> {parseInlineMarkdown(origin.negative_space)}
              </div>
            )}

            {/* Standard / V1 Body Paragraphs */}
            {origin.body_paragraphs && origin.body_paragraphs.length > 0 && (
              <div className="content-body">
                {origin.body_paragraphs.map((para, idx) => (
                  <p key={idx}>{parseInlineMarkdown(para)}</p>
                ))}
              </div>
            )}

            {/* Remember stage bullets */}
            {origin.points && origin.points.length > 0 && (
              <ul className="recap-list">
                {origin.points.map((p, idx) => {
                  if (typeof p === 'object' && p !== null) {
                    const kw = p.keyword || '';
                    const ex = p.explanation || '';
                    return (
                      <li key={idx}>
                        {kw && <strong>{parseInlineMarkdown(kw)} — </strong>}
                        {parseInlineMarkdown(ex)}
                      </li>
                    );
                  }
                  return <li key={idx}>{parseInlineMarkdown(String(p))}</li>;
                })}
              </ul>
            )}

            {/* Apply stage steps */}
            {origin.steps && origin.steps.length > 0 && (
              <ol className="recap-list">
                {origin.steps.map((st, idx) => {
                  const stText = typeof st === 'object' && st !== null ? (st.action || st.text || JSON.stringify(st)) : String(st || '');
                  const cleaned = stText.replace(/^\s*(?:Step\s+)?\d+[\.\s\)]+\s*/i, '');
                  return <li key={idx}>{parseInlineMarkdown(cleaned)}</li>;
                })}
              </ol>
            )}

            {/* Relatable Example (Picture this) if present on non-split slide */}
            {origin.relatable_example && (
              <div className="info-box">
                <span className="label">{tUi('slide.content.pictureThis', uiLocale)}</span>
                <p>"{parseInlineMarkdown(origin.relatable_example)}"</p>
              </div>
            )}
          </>
        )}

        {/* Content More layouts */}
        {isContentMore && !isSplitTail && (
          <>
            {/* Narrative Arc (Gap, Buffer, Collapse) */}
            {origin.narrative_arc === 'the_gap' && (() => {
              const isT7 = origin.tier === 'T7' || slide?.tier === 'T7';
              return (
                <div className="narrative-grid gap-arc">
                  <div className="narrative-card old-workflow">
                    <div className="narrative-header old-workflow">
                      <span className="narrative-icon old-workflow">✕</span>
                      <span className="narrative-title old-workflow">
                        {isT7 ? tUi('slide.narrative.oldWorkflow', uiLocale) : tUi('slide.narrative.before', uiLocale)}
                      </span>
                    </div>
                    <p className="narrative-text old-workflow">
                      {parseInlineMarkdown(origin.old_workflow)}
                    </p>
                  </div>
                  <div className="narrative-card new-workflow">
                    <div className="narrative-header new-workflow">
                      <span className="narrative-icon new-workflow">✓</span>
                      <span className="narrative-title new-workflow">
                        {isT7 ? tUi('slide.narrative.newWorkflow', uiLocale) : tUi('slide.narrative.after', uiLocale)}
                      </span>
                    </div>
                    <p className="narrative-text new-workflow">
                      {parseInlineMarkdown(origin.new_workflow)}
                    </p>
                  </div>
                </div>
              );
            })()}

            {origin.narrative_arc === 'the_buffer' && (() => {
              const isT7 = origin.tier === 'T7' || slide?.tier === 'T7';
              return (
                <div className="narrative-grid buffer-arc">
                  <div className="narrative-card tempting-shortcut">
                    <div className="narrative-header tempting-shortcut">
                      <span className="narrative-title tempting-shortcut">
                        {isT7 ? tUi('slide.narrative.temptingShortcut', uiLocale) : tUi('slide.narrative.theShortcut', uiLocale)}
                      </span>
                    </div>
                    <p className="narrative-text tempting-shortcut">
                      {parseInlineMarkdown(origin.tempting_shortcut)}
                    </p>
                  </div>
                  <div className="narrative-card safety-buffer">
                    <div className="narrative-header safety-buffer">
                      <span className="narrative-title safety-buffer">
                        {isT7 ? tUi('slide.narrative.safetyBuffer', uiLocale) : tUi('slide.narrative.thoughtfulResponse', uiLocale)}
                      </span>
                    </div>
                    <p className="narrative-text safety-buffer">
                      {parseInlineMarkdown(origin.judgment_intervention)}
                    </p>
                  </div>
                </div>
              );
            })()}

            {origin.narrative_arc === 'the_collapse' && (
              <div className="narrative-collapse-container">
                <div className="narrative-collapse-header">
                  <div className="narrative-collapse-header-text">
                    <div className="narrative-collapse-title warning">{tUi('slide.narrative.shortcutTaken', uiLocale)}</div>
                    <div className="narrative-collapse-desc">
                      {parseInlineMarkdown(origin.reasonable_shortcut)}
                    </div>
                  </div>
                </div>
                <div className="narrative-collapse-body">
                  <div className="narrative-collapse-body-text">
                    <div className="narrative-collapse-title crash">{tUi('slide.narrative.catastrophicOutcome', uiLocale)}</div>
                    <div className="narrative-collapse-desc crash">
                      {parseInlineMarkdown(origin.catastrophic_outcome)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Downstream Outcomes */}
            {origin.business_outcomes && (
              <div className="downstream-outcome">
                <strong>{tUi('slide.narrative.downstreamOutcome', uiLocale)}</strong> {parseInlineMarkdown(origin.business_outcomes)}
              </div>
            )}

            {/* Final Warning */}
            {origin.final_warning && (
              <div className="final-warning-container">
                💡 {parseInlineMarkdown(origin.final_warning.replace(/^💡\s*/, '').replace(/^final\s+warning\s*[:-]?\s*/i, ''))}
              </div>
            )}
          </>
        )}

        {/* Split Tail (body paragraphs, points, steps, relatable example, and so what) */}
        {isSplitTail && (
          <>
            {origin.body_paragraphs && origin.body_paragraphs.length > 0 && (
              <div className="content-body">
                {origin.body_paragraphs.map((para, idx) => (
                  <p key={idx}>{parseInlineMarkdown(para)}</p>
                ))}
              </div>
            )}

            {origin.points && origin.points.length > 0 && (
              <ul className="recap-list">
                {origin.points.map((p, idx) => {
                  if (typeof p === 'object' && p !== null) {
                    const kw = p.keyword || '';
                    const ex = p.explanation || '';
                    return (
                      <li key={idx}>
                        {kw && <strong>{parseInlineMarkdown(kw)} — </strong>}
                        {parseInlineMarkdown(ex)}
                      </li>
                    );
                  }
                  return <li key={idx}>{parseInlineMarkdown(String(p))}</li>;
                })}
              </ul>
            )}

            {origin.steps && origin.steps.length > 0 && (
              <ol className="recap-list">
                {origin.steps.map((st, idx) => {
                  const stText = typeof st === 'object' && st !== null ? (st.action || st.text || JSON.stringify(st)) : String(st || '');
                  const cleaned = stText.replace(/^\s*(?:Step\s+)?\d+[\.\s\)]+\s*/i, '');
                  return <li key={idx}>{parseInlineMarkdown(cleaned)}</li>;
                })}
              </ol>
            )}

            {origin.relatable_example && (
              <div className="info-box">
                <span className="label">{tUi('slide.content.pictureThis', uiLocale)}</span>
                <p>"{parseInlineMarkdown(origin.relatable_example)}"</p>
              </div>
            )}
          </>
        )}

        {/* So What takeaway */}
        {soWhat && (
          <div className="content-so-what">
            {parseInlineMarkdown(soWhat)}
          </div>
        )}
      </>
    );

    const slideHeading = slide?.SlideTitle || origin.headline || origin.title || '';

    return (
      <>
        {slide?.Images && slide.Images.length > 0 ? (
          <HtmlContentLayout>
            <HtmlImageContainer>
              <ImagePlaceholder>
                <img
                  src={slide.Images[0]}
                  alt="Slide image"
                  onClick={() => handleImgClick(slide.Images[0])}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    WebkitUserDrag: 'none',
                  }}
                />
              </ImagePlaceholder>
            </HtmlImageContainer>
            <HtmlContentColumn>
              {slideHeading && slideHeading.trim() && (
                <SlideTitle>
                  {highlightWord ? highlightWord('title', slideHeading) : slideHeading}
                </SlideTitle>
              )}
              <HtmlContentWrapper>
                {renderSlideMainContent()}
              </HtmlContentWrapper>
            </HtmlContentColumn>
          </HtmlContentLayout>
        ) : (
          <HtmlContentColumn>
            {slideHeading && slideHeading.trim() && (
              <SlideTitle>
                {highlightWord ? highlightWord('title', slideHeading) : slideHeading}
              </SlideTitle>
            )}
            <HtmlContentWrapper>
              {renderSlideMainContent()}
            </HtmlContentWrapper>
          </HtmlContentColumn>
        )}

        {!onImageClick && (
          <SlideImageLightboxModal
            image={internalLightbox}
            onClose={() => setInternalLightbox(null)}
          />
        )}
      </>
    );
  }

  // Fallback for older HTML/CSS content slides (also used while isEditMode is on)
  const htmlContent = typeof slide?.SlideContent === 'string'
    ? slide.SlideContent
    : Array.isArray(slide?.SlideContent)
      ? slide.SlideContent.join('')
      : String(slide?.SlideContent || '');

  if (slide?.Images && slide.Images.length > 0) {
    return (
      <>
        <HtmlContentLayout>
          <HtmlImageContainer>
            <ImagePlaceholder>
              <img
                src={slide.Images[0]}
                alt="Slide image"
                onClick={() => handleImgClick(slide.Images[0])}
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  WebkitUserDrag: 'none',
                }}
              />
            </ImagePlaceholder>
          </HtmlImageContainer>
          <HtmlContentColumn>
            {isEditMode ? (
              <CombinedEditableSection
                slide={slide}
                isEditMode={isEditMode}
                slideEdits={slideEdits}
                showEnglishContent={showEnglishContent}
                onSave={onSaveEdit}
                onReset={onResetEdit}
                onCancel={onCancelEdit}
                TitleComponent={SlideTitle}
                ContentComponent={HtmlContentWrapper}
              />
            ) : (
              <>
                {slide?.SlideTitle && slide.SlideTitle.trim() && (
                  <SlideTitle>
                    {highlightWord ? highlightWord('title', slide.SlideTitle) : slide.SlideTitle}
                  </SlideTitle>
                )}
                {renderTextWithId('html_content', htmlContent, HtmlContentWrapper, {}, textRendererOptions)}
              </>
            )}
          </HtmlContentColumn>
        </HtmlContentLayout>

        {!onImageClick && (
          <SlideImageLightboxModal
            image={internalLightbox}
            onClose={() => setInternalLightbox(null)}
          />
        )}
      </>
    );
  } else {
    return (
      <HtmlContentColumn>
        {isEditMode ? (
          <CombinedEditableSection
            slide={slide}
            isEditMode={isEditMode}
            slideEdits={slideEdits}
            showEnglishContent={showEnglishContent}
            onSave={onSaveEdit}
            onReset={onResetEdit}
            onCancel={onCancelEdit}
            TitleComponent={SlideTitle}
            ContentComponent={HtmlContentWrapper}
          />
        ) : (
          <>
            {slide?.SlideTitle && slide.SlideTitle.trim() && (
              <SlideTitle>
                {highlightWord ? highlightWord('title', slide.SlideTitle) : slide.SlideTitle}
              </SlideTitle>
            )}
            {renderTextWithId('html_content', htmlContent, HtmlContentWrapper, {}, textRendererOptions)}
          </>
        )}
      </HtmlContentColumn>
    );
  }
};

export default HtmlContentSlide;
